import { Box, Grid } from "@mui/material";
import type { Dayjs } from "dayjs";
import { max } from "lodash";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import { getCalculationPdf, getCalculationPriceList } from "../../api/api";
import { useProducts } from "../admin/products/useProducts";
import { isPlaneTicket } from "../admin/providers/isPlaneTicket";
import type { UserContext } from "../auth/auth";
import AvailabilityReportDialog from "../calculations/availability/AvailabilityReportDialog";
import type {
  CalculationProduct,
  CalculationProductOption,
  SavedCalculationProductOption,
  SavedCalculationVersion,
} from "../calculations/calculationTypes";
import { autoFillCalculation } from "../calculations/calculationUtils";
import dayjs, { ISO_DATE_FORMAT } from "../dayjs";
import { saveAsFile } from "../misc/saveAsFile";
import type { Package } from "../sharedComponents/packages/PackageSelect";
import type { Product } from "../sharedComponents/products/ProductSelect";
import { useAsyncDialog } from "../sharedComponents/useAsyncDialog";
import type { CalculationDraft, CalculationDraftProduct } from "./CalculationDraft";
import { PackageChangeConfirmationDialog } from "./dialogs/PackageChangeConfirmationDialog";
import { useGenerateCalculationPdfDialog } from "./dialogs/useGenerateCalculationPdfDialog";
import { useSelectYearDialog } from "./dialogs/useSelectYearDialog";
import type { CalculationBasicData } from "./sections/BasicData";
import { BasicData } from "./sections/BasicData";
import { CalculationSummary } from "./sections/CalculationSummary";
import { DynamicPricing } from "./sections/DynamicPricing";
import { NavigationBar } from "./sections/NavigationBar";
import { PackageOrProductSection } from "./sections/PackageOrProductSection";
import { ProductList } from "./sections/ProductList";
import { useCalculationProducts } from "./sections/useCalculationProducts";
import { stub } from "./Stub";
import { currencyForProduct } from "./utils/currencyForProduct";

function nextDaysShift(products: { daysShift?: number }[]) {
  const lastDaysShift = products.length > 0 ? max(products.map((p) => p.daysShift ?? 0)) : null;
  return lastDaysShift != null ? lastDaysShift + 1 : 0;
}

function calculationProductsFromPackage(
  selectedPackage: Pick<Package, "id" | "days" | "currency" | "margin">,
): CalculationDraftProduct[] {
  return selectedPackage.days.flatMap((d) =>
    d.products.map((p) => ({
      id: p.id,
      daysShift: d.dayNumber - 1,
      options: p.options.map((id) => ({ id, quantity: 0 })),
    })),
  );
}

export function CalculationEdit(props: {
  onSubmitVersion(value: CalculationDraft): Promise<void>;
  onSubmitOrder?(orderFromProviders: boolean, hasPlaneTickets: boolean): Promise<void>;
  onDelete?(): Promise<void>;
  onEdit?(): void;
  onUpdatePrices?(): void;
  handleRequestQuote?(): Promise<void>;
  value: CalculationDraft;
  versions?: SavedCalculationVersion[];
  selectedVersionIdx?: number;
  onVersionChange?: (idx: number) => void;
  context: UserContext | undefined;
  isEditMode: boolean;
  orderId?: string;
}) {
  const [draft, setDraft] = useState(props.value);
  useEffect(() => {
    setDraft(props.value);
  }, [props.value]);
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const { products: allProducts } = useProducts();
  const { enqueueSnackbar } = useSnackbar();
  const getProductCurrency = (productId: string) => allProducts?.find((p) => p.id === productId)?.currency;

  useEffect(() => {
    if (!draft.products.every((p) => getProductCurrency(p.id) == draft.currency)) {
      enqueueSnackbar("Waluty produktów się nie zgadzają", { variant: "warning" });
    }
  }, [draft.products, allProducts]);

  const calculationProducts = useCalculationProducts(draft);
  const effectiveStartDate = draft.basicData.startDate;
  const dynamicPriceProducts = draft.products
    .filter((p) => calculationProducts.productDetailsMap.get(p.id)?.provider.useDynamicPrices ?? false)
    .map((p) => ({
      productId: p.id,
      date: effectiveStartDate?.add(p.daysShift, "days").format(ISO_DATE_FORMAT) ?? "",
    }));
  const hasDynamicPriceProducts = dynamicPriceProducts.length > 0;
  const hasPlaneTickets = draft.products
    .map((p) => calculationProducts.productDetailsMap.get(p.id)?.provider.productType)
    .some((type) => type && isPlaneTicket(type));

  const hasProductsForQuote = draft.products
    .filter((p) => calculationProducts.productDetailsMap.get(p.id)?.provider.hasEmailRecipients ?? false)
    .some((p) =>
      p.options.some((o: SavedCalculationProductOption | CalculationProductOption) => o.price === "ON_DEMAND"),
    );

  const totalParticipants = draft.basicData.peopleCount + draft.basicData.freePeopleCount;

  const handleBasicDataChange = useCallback((update: Partial<CalculationBasicData>) => {
    setDraft((prev) => {
      return {
        ...prev,
        basicData: {
          ...prev.basicData,
          ...update,
        },
      };
    });
  }, []);

  const handleVersionNameChange = useCallback((versionName: string | null) => {
    setDraft((prev) => ({ ...prev, versionName: versionName ?? undefined }));
  }, []);

  const version = (props.selectedVersionIdx ?? 0) + 1;
  const draftVersionName = draft.versionName;
  const selectYearDialog = useSelectYearDialog();
  const packageChangeConfirmationDialog = useAsyncDialog<boolean>(({ onCancel, onAccept }) => (
    <PackageChangeConfirmationDialog onClose={onCancel} onConfirm={() => onAccept(true)} />
  ));
  const generatePdfDialog = useGenerateCalculationPdfDialog({
    type: draft.basicData.isPackageVersion ? "PACKAGE" : "DEFAULT",
  });

  const handleSelectPackage = async (selectedPackage: Package) => {
    if (draft.products.length > 0) {
      const accept = await packageChangeConfirmationDialog.open();
      if (!accept) return;
    }
    setDraft((prev) => ({
      ...prev,
      basicData: {
        ...prev.basicData,
        margin: selectedPackage.margin,
      },
      packageId: selectedPackage.id.id,
      currency: selectedPackage.currency,
      products: calculationProductsFromPackage(selectedPackage),
    }));
  };

  const handleAutoFill = useCallback(() => {
    setDraft((prev) => {
      const totalParticipants = prev.basicData.peopleCount + prev.basicData.freePeopleCount;
      const productsWithReset = prev.products.map((p) => ({
        ...p,
        options: p.options.map((o) => ({ ...o, quantity: 0 })),
      }));
      return {
        ...prev,
        products: autoFillCalculation(productsWithReset, totalParticipants, allProducts),
      };
    });
  }, [allProducts]);

  const handleAddProduct = useCallback(
    (product: Product) => {
      setDraft((prev) => ({
        ...prev,
        currency: currencyForProduct(allProducts, product.id),
        products: [
          ...prev.products,
          {
            id: product.id,
            daysShift: nextDaysShift(prev.products),
            options: [],
          },
        ],
      }));
    },
    [allProducts],
  );

  const handleReplaceProduct = useCallback((productId: string, replacement: CalculationProduct) => {
    setDraft((prev) => ({
      ...prev,
      products: prev.products.map((p) =>
        p.id === productId ? { ...p, id: replacement.id, options: replacement.options } : p,
      ),
    }));
  }, []);

  const handleDateShift = useCallback((date: Dayjs) => {
    setDraft((prev) => ({
      ...prev,
      basicData: {
        ...prev.basicData,
        startDate: date,
      },
      products: prev.products.map((p) => ({ ...p, options: p.options.map((o) => ({ ...o, price: undefined })) })),
    }));
  }, []);

  async function handleGeneratePdf() {
    const result = await generatePdfDialog.open();
    if (!result) return;
    const data = await getCalculationPdf(draft.calculationId!, version, result === "include-prices");
    saveAsFile(data, `${draft.basicData.name}.pdf`);
  }
  async function handleDownloadPrices() {
    const year = await selectYearDialog.open();
    if (!year) return;
    const data = await getCalculationPriceList(draft.calculationId!, year, version);
    saveAsFile(data, `${draft.basicData.name} - cennik ${year}.pdf`);
  }

  const form = useRef<HTMLFormElement>(null);
  async function handleSubmitVersion() {
    if (!form?.current?.reportValidity()) {
      return;
    }
    await props.onSubmitVersion(draft);
  }

  const saveDisabled = !draft.basicData.name || draft.products.length <= 0;

  const handleSubmitOrder = async (orderFromProviders: boolean) => {
    const onSubmitOrder = props.onSubmitOrder;
    if (onSubmitOrder) {
      await onSubmitOrder(orderFromProviders, hasPlaneTickets);
    }
  };

  const onUpdatePrices = props.onUpdatePrices;

  return (
    <form ref={form}>
      <Box sx={{ mb: 8, animation: "fadeIn 0.3s ease-in-out" }}>
        {/* Pasek Nawigacji Górnej - Sticky */}
        <NavigationBar
          onSubmitVersion={handleSubmitVersion}
          onSubmitOrder={handleSubmitOrder}
          onDelete={props.onDelete}
          onDownloadPrices={handleDownloadPrices}
          onGeneratePDF={handleGeneratePdf}
          onCheckAvailability={hasDynamicPriceProducts ? () => setAvailabilityDialogOpen(true) : undefined}
          onUpdatePrices={hasDynamicPriceProducts && onUpdatePrices ? () => onUpdatePrices() : undefined}
          context={props.context}
          onEdit={props.onEdit}
          isEditMode={props.isEditMode}
          isOrdered={!!props.orderId}
          calculation={draft.basicData}
          isNew={!props.versions?.length}
          orderId={props.orderId}
          saveDisabled={saveDisabled}
          hasProductsForQuote={hasProductsForQuote}
          onRequestQuote={props.handleRequestQuote}
        />

        {/* SEKCJA 1: DANE PODSTAWOWE */}
        <BasicData
          initialValue={draft.basicData}
          onChange={handleBasicDataChange}
          onAutoFill={handleAutoFill}
          onApplyStartDate={(date) => handleDateShift(date ?? draft.basicData.startDate ?? dayjs())}
          versions={props.versions ?? [{ versionName: draft.versionName }]}
          numberOfSavedVersions={props.versions?.length ?? 0}
          selectedVersionIdx={props.selectedVersionIdx ?? 0}
          onVersionChange={props.onVersionChange ?? stub}
          onVersionNameChange={handleVersionNameChange}
          role={props.context?.role}
          isReadonly={!props.isEditMode}
          draftVersionName={draftVersionName}
          packageId={draft.packageId ?? null}
        />

        {/* SEKCJA 2: WYBÓR PAKIETÓW LUB PRODUKTÓW */}
        <PackageOrProductSection
          packageId={draft.packageId}
          onPackageSelected={handleSelectPackage}
          onProductSelected={handleAddProduct}
          onModeChanged={() => setDraft((draft) => ({ ...draft, packageId: undefined }))}
          readonly={!props.isEditMode}
          currency={draft.currency}
        />

        {/* SEKCJA 3: KALKULATOR ORAZ PODSUMOWANIE */}
        <Grid container spacing={4}>
          {/* LEWA STRONA: LISTA PRODUKTÓW */}
          <ProductList
            draft={draft}
            onProductReplace={handleReplaceProduct}
            onChange={(change) => setDraft((prev) => ({ ...prev, ...change }))}
            calculationProducts={calculationProducts}
            isReadOnly={!props.isEditMode}
            hasDynamicPriceProducts={hasDynamicPriceProducts}
          />

          {/* PRAWA STRONA: PODSUMOWANIE KOSZTÓW (Sticky) */}
          <Grid size={{ xs: 12, lg: 3 }}>
            <CalculationSummary
              value={draft}
              productsWithPrices={calculationProducts.productsWithPrices}
              onSubmit={handleSubmitVersion}
              isReadOnly={!props.isEditMode}
              saveDisabled={saveDisabled}
            />
          </Grid>
        </Grid>

        {/* SEKCJA DYNAMIC PRICING (Alternatywne terminy) - pełna szerokość pod gridem */}
        <DynamicPricing
          selectedProducts={draft.products.map((p) => ({
            id: p.id,
            date: draft.basicData.startDate?.add(p.daysShift, "days").format(ISO_DATE_FORMAT) ?? "",
            options: p.options,
          }))}
          availableProducts={allProducts}
          currency={draft.currency}
          margin={draft.basicData.margin}
          numberOfPeople={draft.basicData.peopleCount + draft.basicData.freePeopleCount}
          numberOfPaidPeople={draft.basicData.peopleCount}
          startDate={draft.basicData.startDate}
          onClick={handleDateShift}
          isEditMode={props.isEditMode}
        />
        {packageChangeConfirmationDialog.portal}
        {generatePdfDialog.portal}
        {selectYearDialog.portal}
        {hasDynamicPriceProducts && (
          <AvailabilityReportDialog
            open={availabilityDialogOpen}
            onClose={() => setAvailabilityDialogOpen(false)}
            products={dynamicPriceProducts}
            participantsNumber={totalParticipants}
          />
        )}
      </Box>
    </form>
  );
}
